import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { X, Plus, Loader2, Edit, Save, X as Cancel } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

interface Language {
    id: number;
    code: string;
    description: string;
}

const LanguageAdder = () => {
    const { user } = useAuth();
    const [languages, setLanguages] = useState<Language[]>([]);
    const [code, setCode] = useState("");
    const [description, setDescription] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingList, setLoadingList] = useState(true);
    const [editingId, setEditingId] = useState<number | null>(null);

    // Nyelvek listájának lekérése
    useEffect(() => {
        fetchLanguages();
    }, []);

    const fetchLanguages = async () => {
        try {
            setLoadingList(true);
            const response = await fetch("/api/admin/languages");
            if (response.ok) {
                const data = await response.json();
                setLanguages(data.languages || []);
            }
        } catch (error) {
            console.error("Hiba a nyelvek lekérése során:", error);
        } finally {
            setLoadingList(false);
        }
    };

    const handleAddLanguage = async () => {
        if (!code.trim() || !description.trim()) return;

        try {
            setLoading(true);
            const response = await fetch("/api/admin/languages", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    language: {
                        code: code.toUpperCase(),
                        description: description.toUpperCase(),
                    },
                }),
            });

            if (response.ok) {
                const data = await response.json();
                setLanguages([...languages, data.language]);
                setCode("");
                setDescription("");
            } else if (response.status === 403) {
                alert("Nincs jogosultságod ehhez a művelethez!");
            } else {
                const errorData = await response.json();
                alert(`Hiba: ${errorData.error}`);
            }
        } catch (error) {
            console.error("Hiba a nyelv hozzáadása során:", error);
            alert("Hiba a nyelv hozzáadása során");
        } finally {
            setLoading(false);
        }
    };

    const handleEditLanguage = (lang: Language) => {
        setEditingId(lang.id);
        setCode(lang.code);
        setDescription(lang.description);
    };

    const handleSaveEdit = async () => {
        if (!code.trim() || !description.trim() || editingId === null) return;

        try {
            setLoading(true);
            const response = await fetch(`/api/admin/languages/${editingId}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    language: {
                        code: code.toUpperCase(),
                        description: description.toUpperCase(),
                    },
                }),
            });

            if (response.ok) {
                const data = await response.json();
                setLanguages(languages.map(lang => lang.id === editingId ? data.language : lang));
                setCode("");
                setDescription("");
                setEditingId(null);
            } else if (response.status === 403) {
                alert("Nincs jogosultságod ehhez a művelethez!");
            } else {
                const errorData = await response.json();
                alert(`Hiba: ${errorData.error}`);
            }
        } catch (error) {
            console.error("Hiba a nyelv szerkesztése során:", error);
            alert("Hiba a nyelv szerkesztése során");
        } finally {
            setLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setCode("");
        setDescription("");
    };

    const handleRemoveLanguage = async (id: number) => {
        try {
            const response = await fetch(`/api/admin/languages/${id}`, {
                method: "DELETE",
            });

            if (response.ok) {
                setLanguages(languages.filter((lang) => lang.id !== id));
            } else if (response.status === 403) {
                alert("Nincs jogosultságod a törléshez!");
            } else {
                alert("Hiba a nyelv törlése során");
            }
        } catch (error) {
            console.error("Hiba a nyelv törlése során:", error);
            alert("Hiba a nyelv törlése során");
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleAddLanguage();
        }
    };

    if (!user?.isAdmin) {
        return (
            <div className="w-full max-w-2xl">
                <div className="bg-card rounded-lg border border-border p-6">
                    <p className="text-destructive font-semibold">
                        Nincs jogosultságod a nyelvek kezeléséhez!
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-2xl">
            <div className="bg-card rounded-lg border border-border p-6">
                <h2 className="text-2xl font-bold text-primary mb-6">Nyelvek Kezelése</h2>

                <div className="space-y-4 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Nyelvkód
                        </label>
                        <Input
                            type="text"
                            placeholder="Pl: HU, EN, DE"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            onKeyPress={handleKeyPress}
                            className="uppercase"
                            maxLength={5}
                            disabled={loading}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Nyelv Leírása
                        </label>
                        <Input
                            type="text"
                            placeholder="Pl: MAGYAR, ENGLISH, DEUTSCH"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            onKeyPress={handleKeyPress}
                            className="uppercase"
                            disabled={loading}
                        />
                    </div>

                    <Button
                        onClick={editingId ? handleSaveEdit : handleAddLanguage}
                        className="w-full"
                        disabled={!code.trim() || !description.trim() || loading}
                    >
                        {loading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {editingId ? "Mentés..." : "Hozzáadás..."}
                            </>
                        ) : (
                            <>
                                {editingId ? <Save className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
                                {editingId ? "Mentés" : "Hozzáadás"}
                            </>
                        )}
                    </Button>
                    {editingId && (
                        <Button
                            onClick={handleCancelEdit}
                            variant="outline"
                            className="w-full mt-2"
                            disabled={loading}
                        >
                            <Cancel className="mr-2 h-4 w-4" />
                            Mégse
                        </Button>
                    )}
                </div>

                <div className="space-y-2">
                    <h3 className="font-semibold text-foreground mb-3">Hozzáadott Nyelvek:</h3>
                    {loadingList ? (
                        <p className="text-foreground/60 text-sm">Betöltés...</p>
                    ) : languages.length === 0 ? (
                        <p className="text-foreground/60 text-sm">Még nincs hozzáadott nyelv.</p>
                    ) : (
                        <div className="space-y-2">
                            {languages.map((lang) => (
                                <div
                                    key={lang.id}
                                    className="flex items-center justify-between bg-muted p-3 rounded-md border border-border"
                                >
                                    <div className="flex items-center space-x-4">
                                        <span className="font-mono font-bold text-primary w-12">
                                            {lang.code}
                                        </span>
                                        <span className="text-foreground">{lang.description}</span>
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => handleEditLanguage(lang)}
                                            className="text-blue-500 hover:bg-blue-500/10 p-2 rounded transition-colors"
                                            disabled={editingId !== null}
                                        >
                                            <Edit className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => handleRemoveLanguage(lang.id)}
                                            className="text-destructive hover:bg-destructive/10 p-2 rounded transition-colors"
                                            disabled={editingId !== null}
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LanguageAdder;

